import { CheckCircle2 } from 'lucide-react';

import { REST_LABEL_KEY, type RestPartKind } from '@splitfiesta/core';
import { useTranslation } from '@splitfiesta/i18n';
import { Button, Card } from '@splitfiesta/ui';

import type { BadgeStore } from '@/components/common/StoreBadge';

import { InstallCard } from './InstallCard';
import { LinkFooter } from './LinkFooter';

export interface AfterClaimProps {
  name: string;
  payer: string;
  /** What the person owes, formatted with its currency. */
  total: string;
  /** At most three of the person's items, each at the units they picked. */
  shown: Array<{ id: string; name: string; amount: string }>;
  /** Their part of the tip, tax and units nobody picked yet; null when there is none. */
  rest: { kind: RestPartKind; amount: string } | null;
  more: number;
  picked: number;
  people: number;
  /** Absent once the link stopped: nothing is left to change. */
  onChange?: () => void;
  /** The one install card, or null for the plain footer line. */
  install: { stores: BadgeStore[]; friends: number } | null;
}

const ROW =
  'flex flex-wrap items-baseline gap-x-3 gap-y-0.5 border-b border-gray-100 py-2 last:border-b-0 dark:border-gray-700/60';

export function AfterClaim({
  name,
  payer,
  total,
  shown,
  rest,
  more,
  picked,
  people,
  onChange,
  install,
}: AfterClaimProps) {
  const { t } = useTranslation('claimLink');

  return (
    <div className="space-y-4">
      <Card noHoverEffect>
        <CheckCircle2
          aria-hidden="true"
          className="h-8 w-8 text-green-600 dark:text-green-400"
        />
        <h1 className="mt-2 text-2xl font-bold text-gray-900 dark:text-gray-50">
          {t('done.title', { name })}
        </h1>
        <p className="mt-1 text-base text-gray-700 dark:text-gray-300">
          {t('done.owe', { payer, total })}
        </p>

        {shown.length > 0 || rest ? (
          <ul className="mt-3">
            {shown.map(item => (
              <li key={item.id} className={ROW}>
                <span className="min-w-[45%] flex-1 break-words text-base text-gray-700 dark:text-gray-300">
                  {item.name}
                </span>
                <span className="ml-auto text-base font-medium tabular-nums text-gray-900 dark:text-gray-50">
                  {item.amount}
                </span>
              </li>
            ))}
            {more > 0 ? (
              <li className={`${ROW} text-sm text-gray-600 dark:text-gray-400`}>
                {t('done.more', { count: more })}
              </li>
            ) : null}
            {rest ? (
              <li className={ROW}>
                <span className="min-w-[45%] flex-1 break-words text-base text-gray-700 dark:text-gray-300">
                  {t(`done.rest.${REST_LABEL_KEY[rest.kind]}`)}
                </span>
                <span className="ml-auto text-base font-medium tabular-nums text-gray-900 dark:text-gray-50">
                  {rest.amount}
                </span>
              </li>
            ) : null}
          </ul>
        ) : null}

        <p className="mt-3 text-sm text-gray-600 dark:text-gray-400">
          {t('done.havePicked', { picked, people })}
        </p>
        {onChange ? (
          <Button
            type="button"
            variant="outline"
            onClick={onChange}
            className="mt-3 min-h-11"
          >
            {t('done.change')}
          </Button>
        ) : null}
      </Card>

      {install ? (
        <InstallCard
          payer={payer}
          stores={install.stores}
          friends={install.friends}
        />
      ) : (
        <LinkFooter payer={payer} />
      )}
    </div>
  );
}

export default AfterClaim;
